import { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useDBA } from '@/contexts/DBAContext';
import api, { formatApiError } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { DesktopQuickActionsRail, DesktopRailButton, MobileSheetActions, MobileSheetContent } from '@/components/ui/mobile-sheet';
import { Textarea } from '@/components/ui/textarea';
import { Copy, DollarSign, FileText, Plus, Trash2, CheckCircle, Send, CreditCard, MapPin } from 'lucide-react';
import { mapsUrl } from '@/lib/formatters';
import { ResponsiveRecordCard, ResponsiveRecordList } from '@/components/ui/responsive-record-list';
import { HighVisibilityEmptyState, HighVisibilityPageHeader } from '@/components/ui/high-visibility-mobile';

const invoiceStatusColors = {
  draft: 'bg-zinc-100 text-zinc-700 border border-zinc-200',
  sent: 'bg-sky-100 text-sky-800 border border-sky-200',
  paid: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  overdue: 'bg-red-100 text-red-800 border border-red-200',
  cancelled: 'bg-zinc-100 text-zinc-600 border border-zinc-200',
};

const money = (value) => `$${Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
const inputClass = 'h-14 rounded-2xl border-blue-100 bg-blue-50 text-base font-black text-zinc-950 focus-visible:ring-blue-200 sm:h-11 sm:rounded-md sm:bg-white sm:text-sm sm:font-semibold';

export default function InvoicesPage() {
  const location = useLocation();
  const { dbas, selectedDBA, getDBAName, getDBAColor } = useDBA();
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDetail, setShowDetail] = useState(null);
  const [showCreate, setShowCreate] = useState(null); // job object to create invoice from
  const [lineItems, setLineItems] = useState([]);
  const [taxRate, setTaxRate] = useState(0);
  const [invoiceNotes, setInvoiceNotes] = useState('');
  const [adjustmentNotes, setAdjustmentNotes] = useState('');
  const [error, setError] = useState('');

  const fetchInvoices = useCallback(async () => {
    try {
      const params = {};
      if (selectedDBA) params.dba_id = selectedDBA;
      if (statusFilter !== 'all') params.status = statusFilter;
      const { data } = await api.get('/invoices', { params });
      setInvoices(data);
    } catch { return; }
    finally { setLoading(false); }
  }, [selectedDBA, statusFilter]);

  const fetchSummary = useCallback(async () => {
    try {
      const params = selectedDBA ? { dba_id: selectedDBA } : {};
      const { data } = await api.get('/invoices/dashboard/summary', { params });
      setSummary(data);
    } catch { return; }
  }, [selectedDBA]);

  useEffect(() => { fetchInvoices(); fetchSummary(); }, [fetchInvoices, fetchSummary]);

  useEffect(() => {
    if (!location.state?.openInvoiceId) return;
    const openInvoice = async () => {
      try {
        const { data } = await api.get(`/invoices/${location.state.openInvoiceId}`);
        setShowDetail(data);
        window.history.replaceState({}, '');
      } catch { return; }
    };
    openInvoice();
  }, [location.state]);

  // Handle navigation from Jobs page with createFromJob
  useEffect(() => {
    if (location.state?.createFromJob) {
      openCreateFromJob(location.state.createFromJob);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  useEffect(() => {
    if (location.state?.createForCustomer) {
      openCreateForCustomer(location.state.createForCustomer);
      window.history.replaceState({}, '');
    }
  }, [location.state]);

  const openCreateFromJob = (job) => {
    setShowCreate({ type: 'job', ...job });
    setLineItems([{
      description: job.service_category || job.title || 'Service',
      quantity: 1,
      unit_price: job.estimated_value || 0,
    }]);
    setTaxRate(0);
    setInvoiceNotes('');
    setAdjustmentNotes('');
    setError('');
  };

  const openCreateForCustomer = (customer) => {
    setShowCreate({ type: 'customer', ...customer });
    setLineItems([{ description: 'Service', quantity: 1, unit_price: 0 }]);
    setTaxRate(0);
    setInvoiceNotes('');
    setAdjustmentNotes('');
    setError('');
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { description: '', quantity: 1, unit_price: 0 }]);
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = field === 'description' ? value : parseFloat(value) || 0;
    setLineItems(updated);
  };

  const removeLineItem = (index) => {
    if (lineItems.length <= 1) return;
    setLineItems(lineItems.filter((_, i) => i !== index));
  };

  const subtotal = lineItems.reduce((sum, li) => sum + (li.quantity * li.unit_price), 0);
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount;

  const handleCreateInvoice = async () => {
    if (!showCreate) return;
    setError('');
    try {
      await api.post('/invoices', {
        job_id: showCreate.type === 'job' ? showCreate.id : '',
        customer_id: showCreate.type === 'customer' ? showCreate.id : showCreate.customer_id || '',
        dba_id: showCreate.type === 'customer' ? (showCreate.dba_ids?.[0] || selectedDBA || '') : '',
        line_items: lineItems.map(li => ({ description: li.description, quantity: li.quantity, unit_price: li.unit_price })),
        tax_rate: taxRate,
        notes: invoiceNotes,
        adjustment_notes: adjustmentNotes,
      });
      setShowCreate(null);
      fetchInvoices();
      fetchSummary();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const updateInvoiceStatus = async (id, status, paymentMethod) => {
    try {
      await api.put(`/invoices/${id}/status`, { status, payment_method: paymentMethod || '' });
      fetchInvoices();
      fetchSummary();
      if (showDetail?.id === id) {
        const { data } = await api.get(`/invoices/${id}`);
        setShowDetail(data);
      }
    } catch { return; }
  };

  return (
    <div className="space-y-5 pb-24 animate-fade-in lg:space-y-6 sm:pb-0" data-testid="invoices-page">
      <HighVisibilityPageHeader
        eyebrow="Money desk"
        title="Invoices"
        subtitle="Track sent invoices, payment links, and field-collected payments."
        countLabel={`${invoices.length} invoices`}
        icon={FileText}
        testId="invoices-header"
        titleTestId="invoices-title"
        tone="emerald"
      />

      {/* Summary KPIs */}
      {summary && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="invoice-summary">
          <Card className="rounded-[1.5rem] border border-blue-100 bg-white shadow-sm sm:rounded-md"><CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Total Invoiced</p>
            <p className="text-2xl font-black text-zinc-900 sm:text-xl" data-testid="invoice-summary-total-invoiced">${(summary.total_invoiced || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card className="rounded-[1.5rem] border border-emerald-100 bg-emerald-50 shadow-sm sm:rounded-md"><CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Total Paid</p>
            <p className="text-2xl font-black text-emerald-700 sm:text-xl" data-testid="invoice-summary-total-paid">${(summary.total_paid || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card className="rounded-[1.5rem] border border-amber-100 bg-amber-50 shadow-sm sm:rounded-md"><CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Outstanding</p>
            <p className="text-2xl font-black text-amber-700 sm:text-xl" data-testid="invoice-summary-outstanding">${(summary.outstanding || 0).toLocaleString()}</p>
          </CardContent></Card>
          <Card className="rounded-[1.5rem] border border-sky-100 bg-sky-50 shadow-sm sm:rounded-md"><CardContent className="p-4">
            <p className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">Invoices Sent</p>
            <p className="text-2xl font-black text-sky-700 sm:text-xl" data-testid="invoice-summary-sent-count">{summary.by_status?.sent || 0}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Status filters */}
      <div className="flex gap-2 overflow-x-auto rounded-[1.25rem] border border-blue-100 bg-white p-2 shadow-sm sm:rounded-md" data-testid="invoice-filter-panel">
        {['all', 'sent', 'paid', 'overdue', 'cancelled'].map(s => (
          <button key={s} onClick={() => setStatusFilter(s)}
            className={`min-h-12 shrink-0 rounded-2xl px-4 py-2 text-sm font-black capitalize transition-[transform,background-color] duration-150 active:scale-95 sm:min-h-11 sm:rounded-sm sm:text-xs ${statusFilter === s ? 'bg-blue-600 text-white shadow-sm' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
            data-testid={`invoice-filter-${s}`}>{s === 'all' ? 'All' : s}</button>
        ))}
      </div>

      {/* Invoice list */}
      {loading ? (
        <div className="space-y-3" data-testid="invoices-loading-list">{[1,2,3].map(i => <div key={i} className="h-24 animate-pulse rounded-[1.5rem] bg-blue-100 sm:h-16 sm:rounded-md" data-testid={`invoice-loading-row-${i}`} />)}</div>
      ) : invoices.length === 0 ? (
        <HighVisibilityEmptyState icon={DollarSign} title="No invoices yet" subtitle="Invoices are generated when jobs are completed." testId="invoices-empty-state" />
      ) : (
        <ResponsiveRecordList testId="invoices-list">
          {invoices.map(inv => (
            <ResponsiveRecordCard key={inv.id} testId={`invoice-row-${inv.id}`} accentColor={getDBAColor(inv.dba_id)} title={inv.invoice_number} subtitle={`${inv.customer_name} · ${new Date(inv.created_at).toLocaleDateString()}`} value={money(inv.total)} badge={<Badge variant="secondary" className={`rounded-full px-3 py-1.5 text-xs font-black uppercase ${invoiceStatusColors[inv.status] || ''}`} data-testid={`invoice-status-${inv.id}`}>{inv.status}</Badge>} onOpen={() => setShowDetail(inv)} meta={<span data-testid={`invoice-dba-${inv.id}`}>{getDBAName(inv.dba_id)}</span>} actions={<span className="rounded-2xl bg-slate-50 px-3 py-1 text-sm font-black text-slate-700" data-testid={`invoice-payment-method-${inv.id}`}>{inv.payment_method || 'unpaid'}</span>} />
          ))}
        </ResponsiveRecordList>
      )}

      {/* Create Invoice Dialog */}
      <Dialog open={!!showCreate} onOpenChange={() => setShowCreate(null)}>
        <MobileSheetContent className="sm:!max-w-2xl" data-testid="invoice-create-mobile-sheet">
          <DialogHeader>
            <DialogTitle className="text-3xl font-black font-heading text-zinc-950 sm:text-2xl" data-testid="invoice-create-title">Create Invoice</DialogTitle>
            <DialogDescription>Review and adjust pricing before sending to customer.</DialogDescription>
          </DialogHeader>
          {error && <p className="rounded-2xl border border-red-200 bg-red-50 p-3 text-sm font-black text-red-700" data-testid="invoice-create-error">{error}</p>}
          <div className="space-y-4">
            {showCreate && (
              <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-sm" data-testid="invoice-create-context-card">
                <p className="text-lg font-black leading-tight text-zinc-950" data-testid="invoice-create-context-title">{showCreate.type === 'customer' ? showCreate.name : showCreate.title}</p>
                <p className="mt-1 text-sm font-semibold text-zinc-600" data-testid="invoice-create-context-address">{showCreate.address || 'No address'}{showCreate.address && <a href={mapsUrl(showCreate.address)} target="_blank" rel="noopener noreferrer" className="ml-2 inline-flex min-h-10 min-w-10 items-center justify-center rounded-2xl bg-white align-middle text-blue-700 ring-1 ring-blue-100" data-testid="invoice-create-map-link" aria-label="Open job address in Google Maps"><MapPin className="h-5 w-5" /></a>}{showCreate.scheduled_date ? ` - ${showCreate.scheduled_date}` : ''}</p>
              </div>
            )}
            {showCreate?.type === 'customer' && (
              <div>
                <Label className="text-sm font-black text-slate-800">Business Unit *</Label>
                <select value={showCreate.dba_ids?.[0] || selectedDBA || ''} onChange={(event) => setShowCreate(prev => ({ ...prev, dba_ids: [event.target.value] }))} className="mt-1 h-14 w-full rounded-2xl border border-blue-100 bg-blue-50 px-3 text-base font-black text-zinc-950 sm:h-11 sm:rounded-md sm:bg-white sm:text-sm" data-testid="invoice-customer-dba">
                  <option value="">Select...</option>
                  {dbas.map(dba => <option key={dba.id} value={dba.id}>{dba.name}</option>)}
                </select>
              </div>
            )}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-black uppercase tracking-wider text-blue-700">Line Items</Label>
                <Button type="button" variant="outline" onClick={addLineItem} className="h-12 rounded-2xl border-blue-200 font-black text-blue-700" data-testid="add-line-item">
                  <Plus className="mr-2 h-4 w-4" /> Add Item
                </Button>
              </div>
              <div className="space-y-3" data-testid="invoice-line-items-editor">
                {lineItems.map((li, i) => (
                  <div key={li.id || `${li.description || 'invoice-line'}-${i}`} className="grid grid-cols-2 items-end gap-2 rounded-2xl border border-blue-100 bg-blue-50/60 p-3 sm:flex sm:bg-white" data-testid={`invoice-line-item-row-${i}`}>
                    <div className="col-span-2 flex-1">
                      {i === 0 && <Label className="text-xs font-black text-slate-700">Description</Label>}
                      <Input value={li.description} onChange={(e) => updateLineItem(i, 'description', e.target.value)} placeholder="Service description" className={inputClass} data-testid={`line-item-desc-${i}`} />
                    </div>
                    <div className="sm:w-16">
                      {i === 0 && <Label className="text-xs font-black text-slate-700">Qty</Label>}
                      <Input type="number" value={li.quantity} onChange={(e) => updateLineItem(i, 'quantity', e.target.value)} className={inputClass} data-testid={`line-item-qty-${i}`} />
                    </div>
                    <div className="sm:w-24">
                      {i === 0 && <Label className="text-xs font-black text-slate-700">Price</Label>}
                      <Input type="number" step="0.01" value={li.unit_price} onChange={(e) => updateLineItem(i, 'unit_price', e.target.value)} className={inputClass} data-testid={`line-item-price-${i}`} />
                    </div>
                    <div className="rounded-2xl bg-white px-3 py-2 text-right text-base font-black text-emerald-700 sm:w-24 sm:bg-transparent sm:text-sm" data-testid={`line-item-total-${i}`}>${(li.quantity * li.unit_price).toFixed(2)}</div>
                    {lineItems.length > 1 && (
                      <Button type="button" variant="ghost" onClick={() => removeLineItem(i)} className="h-12 rounded-2xl text-red-500 hover:bg-red-50 hover:text-red-600" data-testid={`remove-line-item-${i}`} aria-label={`Remove line item ${i + 1}`}><Trash2 className="h-5 w-5" /></Button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-3 rounded-2xl border border-emerald-100 bg-emerald-50 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="invoice-create-total-panel">
              <div className="flex items-center gap-2">
                <Label className="text-sm font-black text-emerald-800">Tax %</Label>
                <Input type="number" step="0.1" value={taxRate} onChange={(e) => setTaxRate(parseFloat(e.target.value) || 0)} className="h-12 w-24 rounded-2xl border-emerald-100 bg-white text-base font-black" data-testid="invoice-tax-rate" />
              </div>
              <div className="text-right space-y-1">
                <p className="text-sm font-semibold text-zinc-600">Subtotal: <span className="font-black text-zinc-900" data-testid="invoice-subtotal">${subtotal.toFixed(2)}</span></p>
                {taxAmount > 0 && <p className="text-sm font-semibold text-zinc-600">Tax: <span className="font-black text-zinc-900" data-testid="invoice-tax-amount">${taxAmount.toFixed(2)}</span></p>}
                <p className="text-2xl font-black text-emerald-800" data-testid="invoice-total">Total: ${total.toFixed(2)}</p>
              </div>
            </div>
            <div>
              <Label className="text-sm font-black text-slate-800">Adjustment Notes (internal)</Label>
              <Input value={adjustmentNotes} onChange={(e) => setAdjustmentNotes(e.target.value)} placeholder="e.g., Added extra haul-away charge" className={inputClass} data-testid="invoice-adjustment-notes" />
            </div>
            <div>
              <Label className="text-sm font-black text-slate-800">Customer Notes</Label>
              <Textarea value={invoiceNotes} onChange={(e) => setInvoiceNotes(e.target.value)} rows={3} placeholder="Notes visible to customer..." className="mt-1 min-h-28 rounded-2xl border-blue-100 bg-blue-50 text-base font-semibold sm:rounded-md sm:bg-white sm:text-sm" data-testid="invoice-notes" />
            </div>
            <DialogFooter className="sticky bottom-0 -mx-4 -mb-4 grid gap-2 border-t border-blue-100 bg-white/95 p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)] backdrop-blur sm:static sm:mx-0 sm:mb-0 sm:flex sm:border-0 sm:bg-transparent sm:p-0">
              <Button type="button" variant="outline" onClick={() => setShowCreate(null)} className="h-14 rounded-2xl text-base font-black sm:h-11 sm:text-sm" data-testid="invoice-create-cancel">Cancel</Button>
              <Button onClick={handleCreateInvoice} className="h-14 rounded-2xl bg-emerald-600 text-base font-black hover:bg-emerald-700 sm:h-11 sm:text-sm" data-testid="invoice-create-submit">
                <Send className="w-4 h-4 mr-1" /> Send Invoice
              </Button>
            </DialogFooter>
          </div>
        </MobileSheetContent>
      </Dialog>

      {/* Invoice Detail Dialog */}
      <Dialog open={!!showDetail} onOpenChange={() => setShowDetail(null)}>
        <MobileSheetContent className="sm:!max-w-lg" data-testid="invoice-mobile-detail-sheet">
          {showDetail && (
            <>
              <DialogHeader>
                <DialogTitle className="text-3xl font-black font-heading text-zinc-950 sm:text-2xl" data-testid="invoice-detail-title">{showDetail.invoice_number}</DialogTitle>
                <DialogDescription>{showDetail.customer_name} - {getDBAName(showDetail.dba_id)}</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <Badge variant="secondary" className={`rounded-full px-3 py-1.5 text-sm font-black uppercase ${invoiceStatusColors[showDetail.status] || ''}`} data-testid="invoice-detail-status-badge">{showDetail.status}</Badge>

                <div className="overflow-hidden rounded-2xl border border-blue-100 bg-white" data-testid="invoice-detail-line-items">
                  <div className="grid gap-2 p-3 sm:hidden" data-testid="invoice-detail-mobile-line-list">
                    {showDetail.line_items?.map((li, i) => (
                      <div key={li.id || `${li.description || 'invoice-detail-mobile-line'}-${i}`} className="rounded-2xl bg-blue-50 p-3" data-testid={`invoice-detail-mobile-line-${i}`}>
                        <p className="text-base font-black text-zinc-950">{li.description}</p>
                        <div className="mt-2 flex items-center justify-between text-sm font-bold text-zinc-600"><span>Qty {li.quantity} · {money(li.unit_price)}</span><span className="text-emerald-700" data-testid={`invoice-detail-mobile-line-total-${i}`}>{money(li.total)}</span></div>
                      </div>
                    ))}
                    <div className="rounded-2xl bg-emerald-50 p-3 text-right font-black text-emerald-800" data-testid="invoice-detail-mobile-total">Total {money(showDetail.total)}</div>
                  </div>
                  <table className="hidden w-full text-sm sm:table">
                    <thead><tr className="bg-zinc-50 text-xs text-zinc-500 uppercase"><th className="p-2 text-left">Item</th><th className="p-2 text-right">Qty</th><th className="p-2 text-right">Price</th><th className="p-2 text-right">Total</th></tr></thead>
                    <tbody>
                      {showDetail.line_items?.map((li, i) => (
                        <tr key={li.id || `${li.description || 'invoice-detail-line'}-${i}`} className="border-t border-zinc-100"><td className="p-2">{li.description}</td><td className="p-2 text-right">{li.quantity}</td><td className="p-2 text-right">{money(li.unit_price)}</td><td className="p-2 text-right font-semibold">{money(li.total)}</td></tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t border-zinc-200"><td colSpan="3" className="p-2 text-right text-zinc-500">Subtotal</td><td className="p-2 text-right font-semibold" data-testid="invoice-detail-subtotal">{money(showDetail.subtotal)}</td></tr>
                      {showDetail.tax_amount > 0 && <tr><td colSpan="3" className="p-2 text-right text-zinc-500">Tax ({showDetail.tax_rate}%)</td><td className="p-2 text-right" data-testid="invoice-detail-tax">{money(showDetail.tax_amount)}</td></tr>}
                      <tr className="border-t border-zinc-200 bg-zinc-50"><td colSpan="3" className="p-2 text-right font-bold">Total</td><td className="p-2 text-right text-lg font-bold" data-testid="invoice-detail-total">{money(showDetail.total)}</td></tr>
                    </tfoot>
                  </table>
                </div>

                {showDetail.adjustment_notes && <p className="rounded-2xl bg-amber-50 p-3 text-sm font-semibold text-amber-800" data-testid="invoice-detail-adjustment-notes">Adjustment: {showDetail.adjustment_notes}</p>}
                {showDetail.notes && <p className="rounded-2xl bg-blue-50 p-3 text-sm font-semibold text-blue-900" data-testid="invoice-detail-notes">{showDetail.notes}</p>}

                {/* Square Payment Link */}
                {showDetail.payment_link_url && showDetail.status === 'sent' && (
                  <div className="rounded-2xl border border-sky-200 bg-sky-50 p-4" data-testid="square-payment-link">
                    <p className="text-xs font-bold uppercase tracking-wider text-sky-600 mb-1.5">Square Payment Link</p>
                    <a href={showDetail.payment_link_url} target="_blank" rel="noopener noreferrer"
                      className="text-sm font-bold text-sky-700 underline break-all hover:text-sky-900" data-testid="square-link-url">
                      {showDetail.payment_link_url}
                    </a>
                    <Button variant="outline" className="mt-3 h-12 w-full rounded-2xl text-sm font-black" onClick={() => { navigator.clipboard.writeText(showDetail.payment_link_url); }} data-testid="copy-payment-link">
                      Copy Link to Send to Customer
                    </Button>
                  </div>
                )}

                <MobileSheetActions testId="invoice-sheet-sticky-actions">
                  {showDetail.customer_phone && <a href={`tel:${showDetail.customer_phone}`} className="inline-flex min-h-14 items-center justify-center rounded-2xl bg-blue-600 px-3 text-base font-black text-white" data-testid="invoice-sheet-call-button">Call</a>}
                  {showDetail.address && <a href={mapsUrl(showDetail.address)} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-14 items-center justify-center rounded-2xl border border-blue-200 px-3 text-base font-black text-blue-700" data-testid="invoice-sheet-map-button">Map</a>}
                  {showDetail.payment_link_url && <a href={showDetail.payment_link_url} target="_blank" rel="noopener noreferrer" className="inline-flex min-h-14 items-center justify-center rounded-2xl border border-emerald-200 px-3 text-base font-black text-emerald-700" data-testid="invoice-sheet-payment-link-button">Pay Link</a>}
                  <Button variant="outline" className="h-14 rounded-2xl text-base font-black" onClick={() => navigator.clipboard.writeText(showDetail.invoice_number || '')} data-testid="invoice-sheet-copy-button"><Copy className="mr-2 h-5 w-5" /> Copy #</Button>
                  {showDetail.status === 'sent' && <Button className="h-14 rounded-2xl bg-emerald-600 text-base font-black hover:bg-emerald-700" onClick={() => updateInvoiceStatus(showDetail.id, 'paid', 'square')} data-testid="invoice-mark-paid-square"><CreditCard className="w-5 h-5 mr-2" /> Paid (Square)</Button>}
                  {showDetail.status === 'sent' && <Button variant="outline" className="h-14 rounded-2xl text-base font-black" onClick={() => updateInvoiceStatus(showDetail.id, 'paid', 'cash')} data-testid="invoice-mark-paid-cash"><DollarSign className="w-5 h-5 mr-2" /> Paid (Cash)</Button>}
                </MobileSheetActions>
                <DesktopQuickActionsRail testId="invoice-desktop-quick-actions-rail">
                  {showDetail.customer_phone && <DesktopRailButton href={`tel:${showDetail.customer_phone}`} title="Call customer" testId="invoice-desktop-call-button">☎</DesktopRailButton>}
                  {showDetail.address && <DesktopRailButton href={mapsUrl(showDetail.address)} title="Open map" testId="invoice-desktop-map-button">↗</DesktopRailButton>}
                  {showDetail.payment_link_url && <DesktopRailButton href={showDetail.payment_link_url} title="Open payment link" testId="invoice-desktop-pay-link-button"><CreditCard className="h-4 w-4" /></DesktopRailButton>}
                  <DesktopRailButton onClick={() => navigator.clipboard.writeText(showDetail.invoice_number || '')} title="Copy invoice number" testId="invoice-desktop-copy-button"><Copy className="h-4 w-4" /></DesktopRailButton>
                </DesktopQuickActionsRail>
                {showDetail.status === 'paid' && (
                  <div className="flex items-center gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="invoice-paid-confirmation">
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                    <div>
                      <p className="text-base font-black text-emerald-800">Paid</p>
                      <p className="text-sm font-semibold text-emerald-600" data-testid="invoice-paid-method-date">{showDetail.payment_method} - {showDetail.paid_at && new Date(showDetail.paid_at).toLocaleString()}</p>
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </MobileSheetContent>
      </Dialog>
    </div>
  );
}
